import React from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { Marker, Polyline, Tooltip } from 'react-leaflet';

import {
  CORRIDOR_LINE_WIDTH,
  CORRIDOR_OPACITY,
  vehicleCategoryGlyph,
  type CorridorDisplay
} from '@/features/map-corridors';

/** Legacy paints the vehicle glyph in its primary colour, not in the line's (`_vehiclecategories.scss`). */
const VEHICLE_GLYPH_COLOR = '#34495e';

const svgIcon = (body: string, color: string, size: number, transform = '') =>
  L.divIcon({
    className: 'map-corridor-marker',
    html: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"${transform}>${body}</svg>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });

const ARROW = '<path d="M4 12h13M13 7l5 5-5 5"/>';

const corridorIcon = (corridor: CorridorDisplay) => {
  const glyph = vehicleCategoryGlyph(corridor.vehicleCategoryId);

  // Without a category the corridor keeps legacy's plain arrow, turned to face the
  // way it runs; with one it becomes the vehicle glyph, which carries no direction.
  return glyph
    ? svgIcon(glyph, VEHICLE_GLYPH_COLOR, 18)
    : svgIcon(ARROW, corridor.color, 16, ` style="transform: rotate(${corridor.angle}rad)"`);
};

interface CorridorLayerProps {
  corridors: CorridorDisplay[];
}

export const CorridorLayer = React.memo(function CorridorLayer({ corridors }: CorridorLayerProps) {
  const { t } = useTranslation();

  return (
    <>
      {corridors.map((corridor) => (
        <React.Fragment key={corridor.id}>
          <Polyline
            positions={[
              [corridor.from.lat, corridor.from.lng],
              [corridor.to.lat, corridor.to.lng]
            ]}
            pathOptions={{ color: corridor.color, weight: CORRIDOR_LINE_WIDTH, opacity: CORRIDOR_OPACITY }}
          />

          <Marker position={corridor.markerPosition} icon={corridorIcon(corridor)}>
            <Tooltip direction="top">
              <div>
                {t('welcome.origin.label')}: {corridor.from.name}
              </div>
              <div>
                {t('welcome.destination.label')}: {corridor.to.name}
              </div>
              {corridor.vehicleCategory && (
                <div>
                  {t('vehicleCategory.description.label')}: {corridor.vehicleCategory}
                </div>
              )}
              {corridor.consignments && (
                <div>
                  {t('journey.participatingRequestsStr.label')}: {corridor.consignments}
                </div>
              )}
              {corridor.dispatchDate && (
                <div>
                  {t('welcome.oldestdate.label')}: {corridor.dispatchDate}
                </div>
              )}
            </Tooltip>
          </Marker>
        </React.Fragment>
      ))}
    </>
  );
});
